/* eslint-disable react/prop-types */
import { Check } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ATTRIBUTE_KEYS } from '@/constant/constant';
import { getAttributeLabel } from '@/utils/VendorAttributesUtils';

export const DeliveryTypeSelector = ({
  value,
  onChange,
  getAttributeValue,
  handleAttributeChange,
  validationErrors = {}, // Added default value for safety
  options,
  getDeliveryPreparationTimeValue,
  DELIVERY_PREPARATION_TIMES,
  setDeliveryPreparationTimeValue,
  setValidationErrors,
  setAttributeValue,
}) => {
  const dPrepTimes = getDeliveryPreparationTimeValue();

  return (
    <div className="space-y-3">
      <label className="px-2 text-slate-700 font-bold">I. Choose Delivery Options</label>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-1 px-2">
        {options?.map((option) => {
          const IconComponent = option.icon;
          const isSelected = value === option.value;

          return (
            <button
              key={option?.value}
              type="button"
              onClick={() => onChange(option?.value)}
              className={`relative px-2 py-1.5 rounded-lg border transition-all duration-200 text-left group hover:shadow-md ${
                isSelected
                  ? `border-${option.color}-500 bg-green-200 shadow-md`
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div
                className={`absolute top-2 right-2 size-5 rounded-sm border border-black flex items-center justify-center`}
              >
                {isSelected && <Check className="size-7 text-green-800" />}
              </div>
              <div className="flex items-start space-x-2">
                <div
                  className={`p-2 rounded-lg ${isSelected ? `bg-blue-200` : 'bg-gray-100 group-hover:bg-gray-200'}`}
                >
                  <IconComponent
                    className={`w-6 h-6 ${isSelected ? `text-blue-600` : 'text-gray-600'}`}
                  />
                </div>
                <div className="flex-1">
                  <h5
                    className={`font-medium text-sm ${isSelected ? `text-${option.color}-900` : 'text-gray-900'}`}
                  >
                    {option.label}
                  </h5>
                  <p
                    className={`text-xs ${isSelected ? `text-${option.color}-700` : 'text-gray-600'}`}
                  >
                    {option.description}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {value && value !== 'Self Pickup' && (
        <div className="space-y-3">
          <div className="px-2">
            <Label className="font-bold text-base text-slate-700">
              I(a). Order Delivery Time (for same day delivery)
            </Label>
            <div className="flex flex-wrap gap-3 pt-3">
              {DELIVERY_PREPARATION_TIMES.map((time) => (
                <Button
                  key={time.value}
                  type="button"
                  onClick={() => {
                    if (dPrepTimes.product === time.value) {
                      setDeliveryPreparationTimeValue('product', '');
                    } else {
                      setDeliveryPreparationTimeValue('product', time.value);
                    }
                  }}
                  variant={dPrepTimes.product === time.value ? 'default' : 'outline'}
                  className="focus-visible:ring-offset-0 focus-visible:ring-1"
                >
                  {time.label}
                </Button>
              ))}
            </div>
          </div>
          <div className="px-2">
            <div className="flex items-center justify-between gap-2">
              <Label className="font-bold text-base text-slate-700">
                I(b). Order Delivery Days (other than same day delivery)
              </Label>
              <button
                onClick={() => {
                  setAttributeValue(ATTRIBUTE_KEYS.MIN_DAYS, '');
                  setAttributeValue(ATTRIBUTE_KEYS.MAX_DAYS, '');
                  setValidationErrors((prev) => ({ ...prev, minDays: null, maxDays: null }));
                }}
                className="px-4 py-2 rounded-md bg-green-600 text-white font-bold"
              >
                Reset
              </button>
            </div>
            <div className="flex flex-wrap gap-3 pt-3">
              <div className="flex flex-col flex-1 gap-2">
                <label className="font-semibold">
                  {getAttributeLabel(ATTRIBUTE_KEYS.MIN_DAYS)}
                </label>
                <Input
                  type="number"
                  value={getAttributeValue(ATTRIBUTE_KEYS.MIN_DAYS)}
                  onChange={(e) => handleAttributeChange(ATTRIBUTE_KEYS.MIN_DAYS, e.target.value)}
                />
                {validationErrors.minDays && (
                  <p className="text-xs text-red-600">{validationErrors.minDays}</p>
                )}
              </div>
              <div className="flex flex-col flex-1 gap-2">
                <label className="font-semibold">
                  {getAttributeLabel(ATTRIBUTE_KEYS.MAX_DAYS)}
                </label>
                <Input
                  type="number"
                  value={getAttributeValue(ATTRIBUTE_KEYS.MAX_DAYS)}
                  onChange={(e) => handleAttributeChange(ATTRIBUTE_KEYS.MAX_DAYS, e.target.value)}
                />
                {validationErrors.maxDays && (
                  <p className="text-xs text-red-600">{validationErrors.maxDays}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
